<template>
  <div class="d-print-inline sheet-outer A4 mel-glass-panel mel-glass-panel-inset">
    <section v-for="(section, sectionIndex) in sectionList" :key="sectionIndex" class="sheet">
      <div v-for="(row, rowIndex) in section" :key="rowIndex" class="row mx-0 mel-column-gap">
        <div v-for="signatureItem in row" :key="(signatureItem?.item.id, signatureItem?.lot, signatureItem?.bestBefore, signatureItem?.expiry)" class="col p-2 br-logo mel-card-container">
          <div v-if="signatureItem" :class="{ 'mel-card-duck-content': canRemove }">
            <div class="mel-card-header d-flex">
              <div class="flex-grow-1 mel-card-description">
                <div v-if="signatureItem.item.restockPullType === 2" class="mel-note">
                  <small>{{ $t('instruction.restockPullTypeOriginalPack') }}</small>
                </div>
                <div>
                  <span class="text-primary fw-bold fs-6">{{ addBlanksAfterNonDigitCommaOrDot(signatureItem.item.name) }}</span>
                </div>
              </div>
              <div class="d-flex flex-column align-items-end p-2 ps-3">
                <div>
                  <TradeItemBarcode :itemId="signatureItem.item.id" :itemSignature="signatureItem" :size="'3.5em'" :render-invalid="true" />
                </div>
                <div style="width: 3.5em;">
                  <small class="text-secondary float-end text-nowrap">{{ signatureItem.item.code }}</small>
                </div>
                <div>
                  <small class="text-body text-nowrap">
                    <RestockPullTypeIcon :restock-pull-type="signatureItem.restockPullType" />
                    <template v-if="signatureItem.stockLevel">
                      {{ signatureItem.stockLevel.minQuantity }}/{{ signatureItem.stockLevel.maxQuantity }}&nbsp;{{ signatureItem.item.baseUnitCode }}
                    </template>
                  </small>
                </div>
              </div>
            </div>
            <div class="d-flex">
              <div class="flex-grow-1 fs-5">
                <LotField :item="signatureItem.item" :lot="signatureItem.lot" :show-icon="true" :warn-empty="false" />
                <template v-if="signatureItem.item.trackExpirationDate && signatureItem.expiry">
                  <SimpleExpirationField :item="signatureItem.item" :expiry="signatureItem.expiry" :show-icon="true" />
                </template>
                <template v-else>
                  <SimpleBestBeforeField :item="signatureItem.item" :best-before="signatureItem.bestBefore" :show-icon="true" />
                </template>
              </div>
              <div class="align-content-end">
                <div class="d-flex flex-column">
                  <div class="align-self-end">
                    <span class="fw-bold">{{ signatureItem.supplierItem?.proposedOrderQuantity ?? 1}}</span>
                    <span>&nbsp;</span>
                    <span v-if="signatureItem.supplierItem?.orderUnitCode === signatureItem.item.baseUnitCode">{{ signatureItem.item.baseUnitCode }}</span>
                    <span v-else>{{ signatureItem.supplierItem?.orderUnitCode }}&nbsp;à&nbsp;{{ signatureItem.supplierItem?.factor ?? 1 }}&nbsp;{{ signatureItem.supplierItem?.baseUnitCode }}</span>
                  </div>
                  <div class="align-self-end">
                    <span>{{ signatureItem.supplierItem?.supplier.name}}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="row">
              <div class="col"></div>
            </div>
          </div>
          <div v-if="canRemove && signatureItem" class="mel-card-overlay d-print-none">
            <button class="btn btn-outline-danger btn-lg" @click="emit('remove', signatureItem)">
              <font-awesome-icon :icon="['fas', 'trash']" />
              {{ $t('action.remove') }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { LotField, SimpleBestBeforeField, SimpleExpirationField } from '@/components/fields';
import TradeItemBarcode from "@/components/diagram/TradeItemBarcode.vue";
import RestockPullTypeIcon from "@/components/masterdata/RestockPullTypeIcon.vue";
import { compareOptionalString } from '@/services';
import IItemWithSignatureRaw from '@/models/IItemWithSignatureRaw';
import { useTradeItemsStore, useSupplierItemsStore, useStockLevelsStore, useItemSiteSettingsStore } from "@/stores";
import SupplierItem from '@/models/frontend/SupplierItem';
import ITradeItem from '@/models/responses/ITradeItem';
import IStockLevel from '@/models/responses/IStockLevel';
import { RESTOCK_PULL_TYPE, RestockPullType } from '@/models/Enums';

type LabelItemEntry = IItemWithSignatureRaw & {
  supplierItem?: SupplierItem;
  tradeItem?: ITradeItem;
  stockLevel?: IStockLevel;
  restockPullType: RestockPullType;
  unitName?: string;
}

const props = defineProps({
  items: { type: Object as () => IItemWithSignatureRaw[], required: true },
  isPrefiltered: { type: Boolean, default: false },
  canRemove: { type: Boolean, default: false },
});

const emit = defineEmits<{
  (e: 'remove', signatureItem: IItemWithSignatureRaw): void
}>();

// Adds spaces after commas and dots when followed by non-digit words
// to improve word breaks in text display
const addBlanksAfterNonDigitCommaOrDot = (text: string): string => {
  if (!text) return '';
  return text.replace(/([.,])([^\d\s])/g, '$1 $2');
};

const supplierItemsStore = useSupplierItemsStore();
const tradeItemsStore = useTradeItemsStore();
const stockLevelsStore = useStockLevelsStore();
const itemSiteSettingsStore = useItemSiteSettingsStore();

const sectionList = computed(() => {
  let signatureItems = sortedList.value;
  if (!props.isPrefiltered) {
    signatureItems = signatureItems
    .filter(line => line.item.requiresLabel && (line.item.trackLotNumber || line.item.trackBestBeforeDate || line.item.trackExpirationDate));
  }

  const rowsPerSection = 5;
  const columnsPerRow = 2;

  const sections = [];

  for (let i = 0; i < signatureItems.length; i += rowsPerSection * columnsPerRow) {
    const section = [];
    for (let j = 0; j < rowsPerSection; j++) {
      const row = [];
      let firstRowContent = false;
      for (let k = 0; k < columnsPerRow; k++) {
        const index = i + j * columnsPerRow + k;
        if (index < signatureItems.length) {
          row.push(signatureItems[index]);
          firstRowContent = true;
        }
        else if (firstRowContent) {
          row.push(undefined); // fill the row with empty cells if the first row has content
        }
      }
      section.push(row);
    }
    sections.push(section);
  }

  return sections;
});

const sortedList = computed(() => {
  if (props.items === undefined) {
    return [];
  }
  return [...props.items].sort((a, b) =>
    a.item.name.localeCompare(b.item.name) ||
    compareOptionalString(a.expiry, b.expiry) ||
    compareOptionalString(a.bestBefore, b.bestBefore) ||
    compareOptionalString(a.lot, b.lot)
  ).map((x) => {
    const supplierItem = supplierItemsStore.getByItemId(x.item.id);
    const tradeItem = tradeItemsStore.getByItemIdAndUnitCode(x.item.id, supplierItem?.orderUnitCode ?? x.item.baseUnitCode);
    const unitName = tradeItem?.unitCode ?? x.item.baseUnitCode;
    const stockLevel = stockLevelsStore.getByItemId(x.item.id);
    const restockPullType = itemSiteSettingsStore.getByItemId(x.item.id)?.restockPullType ?? x.item.restockPullType;
    return {
      ...x,
      lot: x.lot, // Ensure lot is included, because it is a get-method
      supplierItem: supplierItem,
      tradeItem: tradeItem,
      stockLevel: stockLevel,
      restockPullType: restockPullType,
      unitName: unitName,
    } as LabelItemEntry;
  });
});
</script>

<style scoped>
.mel-note {
  background-color: #ffdc6a;
}

/*
A4 = 210mm (width) x 297mm (height)
275mm height available
*/
@media print {
  @page {
    size: A4 portrait;
    margin: 1cm;
  }

  body {
    margin: 0;
  }

  .sheet-outer {
    margin: 0;
  }

  .sheet {
    margin: 0;
    overflow: hidden;
    position: relative;
    box-sizing: border-box;
    break-after: page;
    page-break-after: always;
  }

  .sheet:last-child {
    break-after: auto;
    page-break-after: auto;
  }

  .sheet>.row>.col {
    height: 55mm;
    max-width: 90mm;
    /* 275mm / rows */
    border: .5mm dashed #ccc;
  }

  .mel-card-header {
    height: 32mm;
  }

  .mel-card-description {
    max-width: 64mm;
  }

  .mel-column-gap {
    column-gap: 10mm !important;
  }

  .mel-card-logo {
    height: 10mm;
  }

  /* .br-logo {
    position: relative;
  }

  .br-logo::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-image: url("/logo.svg");
    background-size: 20%;
    background-repeat: no-repeat;
    background-position: calc(100% - 10px) calc(100% - 10px);
    opacity: 0.5;
    z-index: -1;
  } */
}

@media screen {
  .sheet-outer {
    margin: 8px;
    padding: 8px;
    background: rgba(var(--bs-secondary-rgb), 0.1);
  }

  .sheet {
    position: relative;
    box-shadow: 4px 4px 8px rgba(0, 0, 0, .3);
    margin: 20px auto;
    max-width: 710px;
  }

  .sheet::before{
    content: "";
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: -2;
    background-color: var(--bs-body-bg);
  }

  .sheet>.row>.col {
    height: 55mm;
    max-width: 339px;
    border: 1px dashed #ccc;
  }

  .mel-card-header {
    height: 120px;
  }

  .mel-card-description {
    max-width: 241px;
  }

  .mel-column-gap {
    column-gap: 32px !important;
  }

  .mel-card-logo {
    height: 40px;
  }

  .mel-card-container {
    position: relative;
    transition: .5s ease;
  }

  .mel-card-overlay {
    transition: .5s ease;
    opacity: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    text-align: center;
  }

  .mel-card-container:hover .mel-card-duck-content {
    opacity: 0.2;
  }

  .mel-card-container:hover .mel-card-overlay {
    opacity: 1 !important;
  }

  /* .br-logo {
    position: relative;
  }

  .br-logo::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-image: url("/logo.svg");
    background-size: 20%;
    background-repeat: no-repeat;
    background-position: calc(100% - 10px) calc(100% - 10px);
    opacity: 0.5;
    z-index: -1;
  } */
}

/* .sheet>.row:first-child>.col {
  border-top: none;
}

.sheet>.row:last-child>.col {
  border-bottom: none;
}

.sheet>.row>.col:first-child {
  border-left: none;
}

.sheet>.row>.col:last-child {
  border-right: none;
} */
</style>
